import { useState, useEffect } from 'react'
import { logError } from 'shared-code/log'
import { DSPageSection } from 'shared-definitions/types'

interface UseTocMonitoringProps {
  current: DSPageSection | null
}

export function useTocMonitoring(items: readonly DSPageSection[]): UseTocMonitoringProps {
  const [current, setCurrent] = useState<DSPageSection | null>(items.length ? items[0] : null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const intersectingAtTop = entry.boundingClientRect.y < window.innerHeight / 2
          if (!intersectingAtTop) {
            continue
          }

          const sectionId = entry.target.getAttribute('id')
          if (!sectionId) {
            continue
          }

          const foundIndex = items.findIndex(cursor => cursor.anchor === sectionId)
          if (foundIndex === -1) {
            continue
          }

          if (!entry.isIntersecting) {
            setCurrent(items[foundIndex])
          } else {
            setCurrent(foundIndex > 0 ? items[foundIndex - 1] : items[0])
          }
        }
      },
      {
        root: null,
        rootMargin: '-10px',
      }
    )

    try {
      for (const item of items) {
        const el = document.querySelector(`#${item.anchor}`)
        if (el) {
          observer.observe(el)
        }
      }
    } catch (e) {
      logError('useTocMonitoring', e)
    }

    return () => observer.disconnect()
  }, [items])

  return { current }
}
